/**
 * Share Dialog Component
 * Provides functionality to create and manage public share links for chats
 */

import { useState, useEffect } from 'react'
import { Copy, Check, Loader2, X, AlertCircle, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { chatService } from '@/services/chat-service'

/**
 * Props for ShareDialog component
 */
interface ShareDialogProps {
  /**
   * Controls dialog visibility
   */
  open: boolean

  /**
   * Callback when dialog should close
   */
  onOpenChange: (open: boolean) => void

  /**
   * ID of the chat to share
   */
  chatId: string
}

/**
 * ShareDialog Component
 * Modal dialog for creating and managing chat share links
 */
export function ShareDialog({ open, onOpenChange, chatId }: ShareDialogProps) {
  /**
   * Share ID from the created share link
   */
  const [shareId, setShareId] = useState<string | null>(null)

  /**
   * Loading state during API operations
   */
  const [loading, setLoading] = useState(false)

  /**
   * Copy button state tracking
   */
  const [copied, setCopied] = useState(false)

  /**
   * Error message display
   */
  const [error, setError] = useState<string | null>(null)

  /**
   * Constructs the full share URL
   */
  const shareUrl = shareId ? `${window.location.origin}/share/${shareId}` : ''

  /**
   * Creates a new share link for the chat
   */
  const handleCreateShare = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await chatService.createChatShare(chatId)

      if (response.success && response.data) {
        setShareId(response.data.id)
      }
    } catch (err) {
      setError('Failed to create share link. Please try again.')
      console.error('Failed to create share:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Copies share URL to clipboard
   */
  const handleCopyLink = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)

      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
      setError('Failed to copy link to clipboard')
    }
  }

  /**
   * Revokes the current share link
   */
  const handleRevokeShare = async () => {
    try {
      setLoading(true)
      setError(null)

      await chatService.deleteChatShare(chatId)

      setShareId(null)
    } catch (err) {
      setError('Failed to revoke share link. Please try again.')
      console.error('Failed to revoke share:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Reset state when dialog is opened
   */
  useEffect(() => {
    if (open) {
      setCopied(false)
      setError(null)
      setLoading(false)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {shareId ? 'Update public link to chat' : 'Share chat'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 bg-muted rounded-lg border border-border">
            <AlertCircle className="size-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                This conversation may include personal information.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Take a moment to check the content before sharing the link.
              </p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg">
              <X className="size-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Privacy Notice */}
          {!shareId && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              Anyone with the link will be able to view this conversation. Messages you add after sharing stay private.
            </p>
          )}

          {/* Share Link Section */}
          {!shareId ? (
            <Button
              onClick={handleCreateShare}
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Creating link...
                </>
              ) : (
                <>
                  <Link2 className="mr-2 size-4" />
                  Create public link
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              {/* URL Input with Copy Button */}
              <div className="flex items-center gap-2 p-2 bg-muted/50 border border-border rounded-lg">
                <Input
                  value={shareUrl}
                  readOnly
                  className="flex-1 border-0 bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                  variant={copied ? "outline" : "default"}
                  size="sm"
                  onClick={handleCopyLink}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <>
                      <Check className="mr-1.5 size-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1.5 size-4" />
                      Copy link
                    </>
                  )}
                </Button>
              </div>

              {/* Privacy Notice for Active Share */}
              <p className="text-sm text-muted-foreground leading-relaxed">
                Messages you add after sharing stay private.
              </p>

              {/* Revoke Link Button */}
              <div className="pt-2 border-t border-border">
                <Button
                  onClick={handleRevokeShare}
                  disabled={loading}
                  variant="destructive"
                  size="lg"
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Revoking link...
                    </>
                  ) : (
                    <>
                      <X className="mr-2 size-4" />
                      Delete public link
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
